import { useState, useCallback, useRef, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate } from "react-router-dom";
import "./App.css";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "./components/ui/dropdown-menu";
import { HeroSearch, SearchBar, ModeToggle } from "./components/HeroSearch";
import { PluginCard } from "./components/PluginCard";
import { PluginDetailModal } from "./components/PluginDetailModal";
import AdminPage from "./components/AdminPage";
import CommunityPage from "./components/CommunityPage";
import LibraryPage from "./components/LibraryPage";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { RotateCcw, Settings, MessagesSquare, Bookmark, LogOut, Library, Loader2, Lightbulb, Share2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ANON_SEARCH_LIMIT = 5;

const REFINEMENTS = {
  search: ["free or cheap only", "more vintage character", "for vocals instead", "for drums instead", "cleaner and more modern"],
  match: ["free or cheap only", "focus on the drums", "focus on the vocals", "focus on the mix bus glue"],
};

const STAGE_LABELS = {
  analyzing: "Interpreting your sound...",
  searching: "Scanning the catalog...",
  ranking: "Curating the best matches...",
  cached: "Instant result",
};

const MATCH_STAGE_LABELS = {
  analyzing: "Deconstructing the record...",
  searching: "Scanning the catalog...",
  ranking: "Building your plugin chain...",
  cached: "Instant result",
};

const groupByRole = (results) => {
  const order = [];
  const groups = {};
  results.forEach((plugin, index) => {
    const role = plugin.role || "Chain";
    if (!groups[role]) { groups[role] = []; order.push(role); }
    groups[role].push({ plugin, index });
  });
  return order.map((role) => [role, groups[role]]);
};

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

function AppRouter() {
  useEffect(() => {
    if (sessionStorage.getItem("ze_visit_tracked")) return;
    sessionStorage.setItem("ze_visit_tracked", "1");
    let vid = localStorage.getItem("ze_visitor_id");
    if (!vid) {
      vid = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      localStorage.setItem("ze_visitor_id", vid);
    }
    fetch(`${API}/api/track/visit`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ visitor_id: vid, path: window.location.pathname }),
    }).catch(() => {});
  }, []);
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/community" element={<CommunityPage />} />
      <Route path="/library" element={<LibraryPage />} />
    </Routes>
  );
}

const HeaderUser = () => {
  const { user, loading, openAuth, logout } = useAuth();
  const navigate = useNavigate();
  if (loading) return <Loader2 className="h-4 w-4 animate-spin text-white/30" />;
  if (!user) {
    return (
      <button data-testid="header-signin-btn" onClick={openAuth}
              className="rounded-lg bg-cyan-500 px-4 py-1.5 text-sm font-semibold text-black transition-colors duration-200 hover:bg-cyan-400">
        Sign in
      </button>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="header-avatar-btn"
                className="flex h-8 w-8 items-center justify-center rounded-full font-heading text-sm font-bold text-black transition-transform duration-150 hover:scale-105"
                style={{ background: user.avatar_color || "#00F0FF" }}>
          {(user.username || user.email || "?").charAt(0).toUpperCase()}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="border-white/10 bg-[#141417] text-white">
        <div className="px-2 py-1.5 text-xs text-white/50">@{user.username || "pick a username"}</div>
        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuItem data-testid="menu-library" onClick={() => navigate("/library")} className="cursor-pointer focus:bg-white/10 focus:text-white">
          <Library className="mr-2 h-4 w-4" /> My Library
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-community" onClick={() => navigate("/community")} className="cursor-pointer focus:bg-white/10 focus:text-white">
          <MessagesSquare className="mr-2 h-4 w-4" /> Community
        </DropdownMenuItem>
        {user.role === "admin" && (
          <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")} className="cursor-pointer focus:bg-white/10 focus:text-white">
            <Settings className="mr-2 h-4 w-4" /> Admin
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="cursor-pointer text-red-400 focus:bg-white/10 focus:text-red-400">
          <LogOut className="mr-2 h-4 w-4" /> Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const SuggestPluginDialog = ({ open, onClose }) => {
  const { user, openAuth } = useAuth();
  const [name, setName] = useState("");
  const [why, setWhy] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!user) { onClose(); openAuth(); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ kind: "suggestion", plugin_name: name, text: why || `Please add ${name}` }),
      });
      if (!res.ok) throw new Error("Failed to submit");
      toast.success("Suggestion sent — thank you!");
      setName(""); setWhy("");
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="suggest-plugin-modal" className="max-w-sm border-white/10 bg-[#111114] text-white">
        <DialogTitle className="font-heading text-xl font-black tracking-tight">Suggest a plugin</DialogTitle>
        <DialogDescription className="text-sm text-white/50">Missing something? Tell us and we'll add it to the catalog.</DialogDescription>
        <form onSubmit={submit} className="space-y-3">
          <input data-testid="suggest-name-input" value={name} onChange={(e) => setName(e.target.value)}
                 placeholder="Plugin name (e.g. Omnisphere)" required
                 className="w-full rounded-md border border-white/10 bg-black/40 px-3.5 py-2.5 text-sm text-white placeholder-white/25 outline-none focus:border-cyan-500/50" />
          <textarea data-testid="suggest-why-input" value={why} onChange={(e) => setWhy(e.target.value)}
                    placeholder="Why should it be in the catalog? (optional)" rows={3}
                    className="w-full rounded-md border border-white/10 bg-black/40 px-3.5 py-2.5 text-sm text-white placeholder-white/25 outline-none focus:border-cyan-500/50" />
          <button data-testid="suggest-submit-btn" disabled={busy}
                  className="w-full rounded-lg bg-cyan-500 py-2.5 text-sm font-semibold text-black transition-colors duration-200 hover:bg-cyan-400 disabled:opacity-50">
            {user ? "Send suggestion" : "Sign in to suggest"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ShareToChatDialog = ({ open, onClose, payload }) => {
  const { user, openAuth } = useAuth();
  const [channels, setChannels] = useState([]);
  const [channel, setChannel] = useState("plugin-talk");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) fetch(`${API}/api/chat/channels`).then((r) => r.json()).then(setChannels).catch(() => {});
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { onClose(); openAuth(); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/chat/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ channel_id: channel, text: comment, share: payload }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Share failed");
      setComment("");
      onClose();
      toast.success(`Shared to #${channel}`, {
        action: { label: "View", onClick: () => navigate("/community") },
      });
    } catch (err) {
      toast.error(String(err.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="share-chat-modal" className="max-w-sm border-white/10 bg-[#111114] text-white">
        <DialogTitle className="font-heading text-xl font-black tracking-tight">Share to community</DialogTitle>
        <DialogDescription className="text-sm text-white/50">
          {payload?.mode === "match" ? "Post this chain" : "Post this search"} — "{payload?.query}" — into a channel. Anyone can open the full results with one click.
        </DialogDescription>
        <form onSubmit={submit} className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {channels.map((ch) => (
              <button type="button" key={ch.id} data-testid={`share-channel-${ch.id}`}
                      onClick={() => setChannel(ch.id)}
                      className={`rounded-full px-3 py-1.5 font-mono text-[11px] transition-colors duration-150 ${
                        channel === ch.id ? "bg-cyan-500 text-black" : "bg-white/5 text-white/50 hover:text-white"
                      }`}>
                # {ch.name}
              </button>
            ))}
          </div>
          <textarea data-testid="share-comment-input" value={comment} onChange={(e) => setComment(e.target.value)}
                    placeholder="Add a comment (optional)" rows={2}
                    className="w-full rounded-md border border-white/10 bg-black/40 px-3.5 py-2.5 text-sm text-white placeholder-white/25 outline-none focus:border-cyan-500/50" />
          <button data-testid="share-submit-btn" disabled={busy}
                  className="w-full rounded-lg bg-cyan-500 py-2.5 text-sm font-semibold text-black transition-colors duration-200 hover:bg-cyan-400 disabled:opacity-50">
            {user ? `Share to #${channel}` : "Sign in to share"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

function Home() {
  const { user, openAuth } = useAuth();
  const [view, setView] = useState("hero");
  const [mode, setMode] = useState("search");
  const [pluginCount, setPluginCount] = useState(null);
  const [query, setQuery] = useState("");
  const [stage, setStage] = useState(null);
  const [intent, setIntent] = useState("");
  const [analysis, setAnalysis] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [results, setResults] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [anonUsed, setAnonUsed] = useState(() => parseInt(localStorage.getItem("ze_anon_searches") || "0", 10));
  const abortRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/stats`).then((r) => r.json()).then((s) => setPluginCount(s.plugins)).catch(() => {});
    const raw = sessionStorage.getItem("pg_restore");
    if (raw) {
      sessionStorage.removeItem("pg_restore");
      try {
        const item = JSON.parse(raw);
        setMode(item.mode || "search");
        setQuery(item.query);
        setIntent(item.intent || "");
        setAnalysis(item.analysis || "");
        setResults(item.results || []);
        setView("results");
      } catch { /* ignore corrupt restore */ }
    }
  }, []);

  const runSearch = useCallback(async (q, searchMode) => {
    if (!q.trim()) return;
    if (!user) {
      const used = parseInt(localStorage.getItem("ze_anon_searches") || "0", 10);
      if (used >= ANON_SEARCH_LIMIT) {
        toast.info("You've used your 5 free searches", {
          description: "Create a free account to keep searching — it's quick.",
        });
        openAuth();
        return;
      }
      const next = used + 1;
      localStorage.setItem("ze_anon_searches", String(next));
      setAnonUsed(next);
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setView("results");
    setQuery(q);
    setStage("analyzing");
    setIntent("");
    setAnalysis("");
    setResults(null);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/api/search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ query: q, mode: searchMode }),
        signal: controller.signal,
      });
      if (!res.ok) throw new Error(`Search failed (${res.status})`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const ev = JSON.parse(line.slice(6));
          if (ev.type === "stage") setStage(ev.stage);
          else if (ev.type === "intent") setIntent(ev.summary);
          else if (ev.type === "delta") setAnalysis((a) => a + ev.text);
          else if (ev.type === "analysis") setAnalysis(ev.text);
          else if (ev.type === "results") { setResults(ev.plugins); setStage(null); }
          else if (ev.type === "error") throw new Error(ev.message);
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") {
        toast.error("Search failed", { description: e.message });
        setStage(null);
        setResults([]);
      }
    } finally {
      setStreaming(false);
    }
  }, [user, openAuth]);

  const reset = () => {
    abortRef.current?.abort();
    setView("hero");
    setQuery("");
    setResults(null);
    setAnalysis("");
    setIntent("");
    setStage(null);
  };

  const saveToLibrary = async () => {
    if (!user) { openAuth(); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/library`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          kind: mode === "match" ? "chain" : "search",
          query, mode, intent, analysis, results: results || [],
        }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Save failed");
      toast.success(mode === "match" ? "Chain saved to your library" : "Search saved to your library");
    } catch (e) {
      toast.error(String(e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grain min-h-screen bg-[#09090B] text-white">
      <Toaster theme="dark" position="bottom-right" />
      <header className="sticky top-0 z-40 border-b border-white/10 bg-black/60 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <button data-testid="logo-home-btn" onClick={reset} className="flex items-center gap-2.5">
            <img src="/logo-mark.png" alt="ZoundEngineer" className="h-8 w-8" />
            <span className="font-heading text-lg font-800 font-bold tracking-tight">
              Zound<span className="text-cyan-400">Engineer</span>
            </span>
          </button>
          <div className="flex items-center gap-4">
            <span className="hidden font-mono text-xs uppercase tracking-[0.2em] text-white/40 md:block">
              {pluginCount ? `${pluginCount.toLocaleString()} plugins` : "1,300+ plugins"}
            </span>
            <Link data-testid="community-link" to="/community"
                  className="flex items-center gap-1.5 text-sm text-white/60 transition-colors duration-200 hover:text-white">
              <MessagesSquare className="h-4 w-4" /> <span className="hidden sm:inline">Community</span>
            </Link>
            <HeaderUser />
          </div>
        </div>
      </header>

      {!user && anonUsed > 0 && (
        <div data-testid="anon-search-banner" className="border-b border-cyan-500/10 bg-cyan-500/5">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-6 py-2">
            <span className="font-mono text-[11px] uppercase tracking-widest text-cyan-300/80">
              {anonUsed >= ANON_SEARCH_LIMIT
                ? "Free searches used up"
                : `${ANON_SEARCH_LIMIT - anonUsed} free ${ANON_SEARCH_LIMIT - anonUsed === 1 ? "search" : "searches"} left`}
            </span>
            <button
              data-testid="anon-banner-signin"
              onClick={openAuth}
              className="rounded-md bg-cyan-500 px-3 py-1 text-xs font-semibold text-black transition-colors duration-200 hover:bg-cyan-400"
            >
              Sign in for unlimited
            </button>
          </div>
        </div>
      )}

      {view === "hero" ? (
        <HeroSearch mode={mode} onModeChange={setMode} onSearch={(q) => runSearch(q, mode)} />
      ) : (
        <main className="relative z-10 mx-auto max-w-6xl px-6 pb-24 pt-8">
          <div className="mb-4">
            <ModeToggle
              mode={mode}
              disabled={streaming}
              onChange={(m) => { setMode(m); reset(); }}
            />
          </div>
          <SearchBar key={query} initial={query} mode={mode} onSearch={(q) => runSearch(q, mode)} compact />

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {REFINEMENTS[mode].map((r) => (
              <button
                key={r}
                data-testid={`refine-chip-${r.replace(/\s+/g, "-")}`}
                disabled={streaming}
                onClick={() => runSearch(`${query}, ${r}`, mode)}
                className="rounded-full bg-white/5 px-3.5 py-1.5 text-xs text-white/60 transition-colors duration-200 hover:bg-white/10 hover:text-white disabled:opacity-40"
              >
                + {r}
              </button>
            ))}
            <button
              data-testid="suggest-plugin-btn"
              onClick={() => setSuggestOpen(true)}
              className="flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/5 px-3.5 py-1.5 text-xs text-amber-300/80 transition-colors duration-200 hover:bg-amber-500/15 hover:text-amber-300"
            >
              <Lightbulb className="h-3 w-3" /> Missing something?
            </button>
          </div>

          {intent && (
            <div data-testid="intent-summary" className="anim-fade-up mt-8 rounded-md border border-cyan-500/20 bg-cyan-500/5 px-5 py-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-400">
                {mode === "match" ? "Record identified" : "Genie heard"}
              </div>
              <p className="mt-1.5 text-sm leading-relaxed text-white/85">{intent}</p>
            </div>
          )}

          {stage && (
            <div data-testid="search-stage" className="mt-8 flex items-center gap-3 text-sm text-white/50">
              <span className="flex gap-1">
                <span className="thinking-dot h-2 w-2 rounded-full bg-cyan-400" />
                <span className="thinking-dot h-2 w-2 rounded-full bg-cyan-400" />
                <span className="thinking-dot h-2 w-2 rounded-full bg-cyan-400" />
              </span>
              <span className="font-mono text-xs uppercase tracking-widest">
                {(mode === "match" ? MATCH_STAGE_LABELS : STAGE_LABELS)[stage] || stage}
              </span>
            </div>
          )}

          {analysis && (
            <div data-testid="ai-analysis" className="anim-fade-up mt-6">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                {mode === "match" ? "Production breakdown" : "Analysis"}
              </div>
              <p className="mt-2 max-w-3xl font-mono text-[13px] leading-relaxed text-white/75">
                {analysis}
                {streaming && !results && <span className="cursor-blink" />}
              </p>
            </div>
          )}

          {results && (
            <>
              <div className="mt-10 flex items-center justify-between">
                <h2 className="font-heading text-lg font-bold tracking-tight text-white/90">
                  {results.length === 0
                    ? "No matches found"
                    : mode === "match"
                    ? "Your recreation chain"
                    : `Top ${results.length} matches`}
                </h2>
                <div className="flex items-center gap-3">
                  {results.length > 0 && (
                    <>
                      <button
                        data-testid="share-community-btn"
                        onClick={() => (user ? setShareOpen(true) : openAuth())}
                        className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3.5 py-1.5 text-xs font-semibold text-white/70 transition-colors duration-200 hover:border-cyan-500/40 hover:text-white"
                      >
                        <Share2 className="h-3.5 w-3.5" />
                        Share to community
                      </button>
                      <button
                        data-testid="save-library-btn"
                        disabled={saving}
                        onClick={saveToLibrary}
                        className="flex items-center gap-1.5 rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-3.5 py-1.5 text-xs font-semibold text-cyan-300 transition-colors duration-200 hover:bg-cyan-500/20 disabled:opacity-50"
                      >
                        <Bookmark className="h-3.5 w-3.5" />
                        {mode === "match" ? "Save this chain" : "Save this search"}
                      </button>
                    </>
                  )}
                  <button data-testid="new-search-btn" onClick={reset} className="flex items-center gap-1.5 text-xs text-white/40 transition-colors duration-200 hover:text-white">
                    <RotateCcw className="h-3.5 w-3.5" /> New search
                  </button>
                </div>
              </div>
              {mode === "match" && results.length > 0 ? (
                <div data-testid="results-grid" className="mt-5 space-y-10">
                  {groupByRole(results).map(([role, plugins], gi) => (
                    <section key={role} data-testid={`chain-section-${gi}`}>
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400">{role}</span>
                        <span className="h-px flex-1 bg-white/10" />
                      </div>
                      <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {plugins.map(({ plugin, index }) => (
                          <PluginCard key={`${plugin.id}-${index}`} plugin={plugin} index={index} onOpen={() => setSelectedId(plugin.id)} />
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              ) : (
                <div data-testid="results-grid" className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {results.map((p, i) => (
                    <PluginCard key={`${p.id}-${i}`} plugin={p} index={i} onOpen={() => setSelectedId(p.id)} />
                  ))}
                </div>
              )}
            </>
          )}
        </main>
      )}

      <PluginDetailModal pluginId={selectedId} onClose={() => setSelectedId(null)} />
      <SuggestPluginDialog open={suggestOpen} onClose={() => setSuggestOpen(false)} />
      <ShareToChatDialog open={shareOpen} onClose={() => setShareOpen(false)}
                         payload={{ query, mode, intent, analysis, results: results || [] }} />
    </div>
  );
}
